<template>
  <div
    class="comment-card"
    :class="{ highlighted: isHighlighted }"
    :data-comment-id="commentId"
  >
    <div class="comment-card__user-wrapper">
      <ChipAvatar :account="createdBy" />
      <p class="text-caption grey--text mt-1 mb-0">
        {{ commentDate }}
      </p>
    </div>
    <div class="comment-card__contextual-menu">
      <ContextualMenu
        v-if="Boolean(isCommentOwner || canEdit)"
        :options="options"
        @optionClicked="optionClicked"
      />
    </div>

    <v-skeleton-loader type="paragraph" v-if="isTranslating" />

    <div v-else style="white-space: pre-line">
      <template v-for="(chunk, index) in contentChunks">
        <CardMention
          v-if="REGEXES.MENTION.test(chunk)"
          :key="chunk + index"
          :account="getAccountFromMention(chunk)"
        />
        <a
          v-else-if="REGEXES.URL.test(chunk)"
          target="__blank"
          rel="noreferrer noopener"
          :key="chunk + index"
          :href="getLinkWithProtocol(chunk)"
          >{{ chunk }}
        </a>
        <template v-else>{{ `${chunk}` }}</template>
      </template>
    </div>

    <div class="d-flex mt-3 mb-0">
      <button
        v-if="isTranslationEnabled"
        class="blue--text mr-3 caption cursor-pointer d-inline-block"
        @click="translate"
      >
        {{ translationText }}
      </button>

      <button
        class="blue--text caption cursor-pointer d-inline-block"
        @click="$emit('reply')"
      >
        {{ $t('comments.reply') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import _ from 'lodash'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import LANGUAGES_DICTIONARY from '@/locales/dictionary.json'

import ContextualMenu from '@/components/Core/ContextualMenu.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'

import CardMention from './CardMention.vue'

const REGEXES = {
  SPACES_NOT_CONSIDERING_NEW_LINES: /(\s)/gm,
  MENTION: /@[0-9a-fA-F]{24}/,
  URL: /(?:(?:https?|ftp):\/\/)?[\w/\-?=%.]+\.[\w/\-&?=%#.]+/i,
  SPACED_MENTIONS: /@\s/g, // After translating a comment with mentions, the "@" character and the account id gets separated by Google Translate. We manually fix this using the "replace" method passing this regex as parameter.
}

export default {
  name: 'CommentCard',

  mixins: [mixin, trackerMixin],

  components: {
    ContextualMenu,
    CardMention,
    ChipAvatar,
  },

  props: {
    userId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    comment: {
      type: String,
      required: true,
    },
    ownerName: {
      type: String,
      required: true,
    },
    commentDate: {
      type: String,
      required: true,
    },
    canEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCommentOwner: {
      type: Boolean,
      required: false,
      default: false,
    },
    commentLanguage: {
      type: String,
      required: false,
    },
    commentId: {
      type: String,
      required: true,
    },
    isHighlighted: Boolean,

    createdBy: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isTranslating: false,
      isTranslated: false,
      accountsMap: _.keyBy(this.accounts, 'id'),
      REGEXES,
    }
  },

  computed: {
    ...mapGetters(['language', 'availableFeatures']),
    ...mapState('comments', {
      comments: state => _.orderBy(state?.comments, 'createdAt', 'asc'),
    }),

    contentChunks() {
      return (
        this.comment
          ?.replace?.(REGEXES.SPACED_MENTIONS, '@')
          .split?.(REGEXES.SPACES_NOT_CONSIDERING_NEW_LINES) ?? []
      )
    },

    options() {
      return [
        {
          id: 'delete',
          tid: 'commentCard-button-delete',
          text: this.$t('common.button.delete'),
          icon: 'mdi-delete-outline',
        },
      ]
    },

    isTranslationEnabled() {
      if (
        !this.commentLanguage ||
        this.commentLanguage === this.language ||
        !LANGUAGES_DICTIONARY?.['primary-dialects'][this.commentLanguage]
      )
        return false

      return this.availableFeatures.includes('translation')
    },

    translationText() {
      return this.isTranslated
        ? this.$t('common.see_original_lang', {
            lang: this.$t(`languages.${this.getLanguageName()}`),
          })
        : this.$t('common.translate')
    },
  },

  methods: {
    ...mapActions({
      getCommentById: 'comments/getCommentById',
      translateComment: 'comments/translateComment',
    }),

    ...mapMutations({
      setComments: 'comments/SET_COMMENTS',
    }),

    optionClicked(id) {
      if (id === 'delete') {
        this.$emit('onDeleteComment')
      }
    },

    getLanguageName() {
      const availableLanguages = this.getLanguagesForTranslationKeys()

      return (
        availableLanguages
          .find(lang => this.commentLanguage === lang.value)
          ?.name.toLowerCase() ??
        this.commentLanguage ??
        ''
      )
    },

    async translate() {
      try {
        this.isTranslating = true
        if (this.isTranslated) {
          this.isTranslated = false
          const response = await this.getCommentById({
            organizationId: this.organizationId,
            commentId: this.commentId,
          })
          const comment = response.data
          this.setComment(comment)
          return comment
        }
        const fieldsToTranslate = ['content']

        const comment = await this.translateComment({
          commentId: this.commentId,
          organizationId: this.organizationId,
          targetLanguage: this.language,
          fieldsToTranslate,
        })
        this.setComment(comment)
        this.trackTranslationComment()
        this.isTranslated = true
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isTranslating = false
      }
    },

    setComment(comment) {
      const commentIndex = this.comments.findIndex(cm => cm.id == comment.id)

      const comments = this.comments
      comments[commentIndex] = comment

      this.setComments(comments)
    },

    getAccountFromMention(mention) {
      const accountId = mention.slice(1)
      return this.accountsMap[accountId] ?? {}
    },

    getLinkWithProtocol(url) {
      if (/^[http|https]/.test(url)) return url
      return `https://${url}`
    },
  },
}
</script>

<style lang="scss" scoped>
.comment-card {
  border-radius: 4px;
  box-shadow: 0px 1px 2px rgba(120, 135, 155, 0.3);
  margin: 12px 8px;
  padding: 16px;
  position: relative;
  background-color: white;
}

.highlighted {
  animation: highlight 4000ms ease-out;
}

.comment-card__user-wrapper {
  margin-bottom: 12px;
}

.comment-card__contextual-menu {
  position: absolute;
  top: 8px;
  right: 8px;
}

@keyframes highlight {
  0% {
    background-color: var(--v-blue-lighten4);
  }
  100% {
    background-color: white;
  }
}
</style>
